/* Restyle the app's editor without replacing its canvas or action handlers. */
(() => {
  const selector = '.tee-dialog-image-placement';
  let activePhoto;
  let frame;
  let serial = 0;
  const previewObservers = new Map();
  const originalGeometry = new WeakMap();
  const actions = ['Zoom out', 'Zoom in', 'Rotate left', 'Rotate right', 'Move left', 'Move right', 'Move up', 'Move down'];

  function trackPhoto(event) {
    const photo = event.target.closest?.('#teeFormInputs .tee-photo');
    if (photo) activePhoto = photo;
  }
  document.addEventListener('click', trackPhoto, true);
  document.addEventListener('change', trackPhoto, true);

  function refresh() {
    frame = null;
    for (const [preview, observer] of previewObservers) {
      if (!preview.isConnected) { observer.disconnect(); previewObservers.delete(preview); }
    }
    document.querySelectorAll(selector).forEach(dialog => {
      const inner = dialog.querySelector('.tee-dialog-inner');
      const footer = dialog.querySelector('.tee-dcontent-footer');
      if (!inner || !footer) return;
      const fresh = !dialog.classList.contains('gp-photo-editor');
      dialog.classList.add('gp-photo-editor');
      const preview = dialog.querySelector('.tee-ip-preview--with-mockup');
      const stage = preview?.querySelector('.tee-ip-preview-inner');
      const mockup = stage?.querySelector('.tee-preview__mockup');
      // Auto-focus/multiple-photo modes have their own transformed toolbar layout.
      if (mockup && !preview.matches('.tee-ip-preview--focus-zoom') && !preview.querySelector('.tee-ip-photos')) {
        const fit = () => {
          const currentMockup = stage.querySelector('.tee-preview__mockup');
          const drawing = currentMockup?.querySelector('.tee-mockup');
          if (!drawing) return;
          let geometry = originalGeometry.get(drawing);
          if (!geometry) {
            geometry = { width: parseFloat(drawing.style.width), height: parseFloat(drawing.style.height), areas: new WeakMap() };
            originalGeometry.set(drawing, geometry);
          }
          if (!(geometry.width > 0 && geometry.height > 0)) return;
          const size = Math.min(preview.clientWidth, preview.clientHeight);
          const scale = Math.min(size / geometry.width, size / geometry.height);
          if (!(scale > 0)) return;
          let changed = false;
          const set = (node, key, value) => {
            const before = node.style[key];
            if (before !== value) {
              node.style[key] = value;
              if (node.style[key] !== before) changed = true;
            }
          };
          set(drawing, 'width', `${geometry.width * scale}px`);
          set(drawing, 'height', `${geometry.height * scale}px`);
          drawing.querySelectorAll('.tee-printarea').forEach(area => {
            let original = geometry.areas.get(area);
            if (!original) {
              original = { left: parseFloat(area.style.left) || 0, top: parseFloat(area.style.top) || 0,
                width: parseFloat(area.style.width), height: parseFloat(area.style.height), transform: area.style.transform };
              geometry.areas.set(area, original);
            }
            if (!(original.width > 0 && original.height > 0)) return;
            // The native editor reads these transforms to invert pointer coordinates.
            // Scaling the outer dialog instead would make dragging inaccurate.
            set(area, 'left', `${original.left * scale - original.width * (1 - scale) / 2}px`);
            set(area, 'top', `${original.top * scale - original.height * (1 - scale) / 2}px`);
            set(area, 'transform', `scale(${scale}) ${original.transform === 'none' ? '' : original.transform}`.trim());
          });
          if (changed) requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        };
        fit();
        if (!previewObservers.has(preview)) {
          const observer = new ResizeObserver(fit);
          observer.observe(preview);
          previewObservers.set(preview, observer);
        }
      }

      if (!inner.querySelector('.gp-editor-title')) {
        const title = document.createElement('h2');
        title.className = 'gp-editor-title';
        title.id = `gp-editor-title-${++serial}`;
        title.textContent = 'Preview';
        inner.prepend(title);
        dialog.querySelector('[role="dialog"]')?.setAttribute('aria-labelledby', title.id);
      }
      const save = footer.querySelector('.tee-btn--primary > span');
      if (save?.textContent.trim() === 'Save') save.textContent = 'Apply';

      dialog.querySelectorAll('.tee-toolbar').forEach(toolbar => {
        const buttons = toolbar.querySelectorAll(':scope > button');
        if (buttons.length !== actions.length) return;
        buttons.forEach((button, index) => {
          button.setAttribute('aria-label', actions[index]);
          button.title = actions[index];
        });
        // Keep the original buttons/handlers, but give the toolbar its own row
        // outside the app's absolutely positioned preview overlay.
        let tools = inner.querySelector('.gp-editor-tools');
        if (!tools) {
          tools = document.createElement('div');
          tools.className = 'gp-editor-tools';
          tools.setAttribute('role', 'group');
          tools.setAttribute('aria-label', 'Photo adjustments');
          (inner.querySelector('.gp-editor-replace') || footer).before(tools);
        }
        if (toolbar.parentElement !== tools) tools.append(toolbar);
      });

      const close = inner.querySelector('.tee-dialog-close');
      if (close && !close.hasAttribute('role')) {
        close.setAttribute('role', 'button');
        close.setAttribute('aria-label', 'Close photo editor');
        close.tabIndex = 0;
        close.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); close.click(); }
        });
      }

      let replace = inner.querySelector('.gp-editor-replace');
      if (!replace) {
        const photo = activePhoto;
        replace = document.createElement('button');
        replace.type = 'button';
        replace.className = 'gp-editor-replace';
        replace.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M7 18H5a4 4 0 0 1-.8-7.9A7 7 0 0 1 18 8a5 5 0 0 1 1 9.9h-2M12 21V10m-4 4 4-4 4 4"/></svg><span>Replace Photo</span>';
        replace.addEventListener('click', () => {
          const input = dialog.querySelector('.tee-image-placement-replace-input') ||
            (photo?.isConnected && photo.querySelector('input[type="file"]'));
          if (input && !input.disabled) input.click();
        });
        replace.hidden = !dialog.querySelector('.tee-image-placement-replace-input') && !photo?.isConnected;
        footer.before(replace);
      }

      // Let the app measure the new preview area and retain accurate drag/zoom coordinates.
      if (fresh) requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(refresh); }
  new MutationObserver(records => {
    if (records.some(record => record.target.closest?.(selector) ||
      [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
        (node.matches(selector) || node.querySelector(selector))))) schedule();
  }).observe(document.body, { childList: true, subtree: true });
  refresh();
})();
